import { useCallback, useRef, useState } from "react";
import { debounce } from "../classes/utils/OptimizationUtils";
import {
  createRadiodensityMeasurementInDatabase,
  updateRadiodensityMeasurementInDatabase,
} from "../services/RadiodensityMeasurementsService";

/**
 * Custom hook to handle auto-sync of radiodensity measurements to database.
 * Uses a lock pattern to prevent race conditions.
 * After POST, calls refetch to get updated measurements immediately.
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.digitalTwinId - The digital twin ID
 * @param {Object} params.measurementsByVertebra - Existing measurements indexed by vertebra ID
 * @param {Function} params.refetchMeasurements - Apollo refetch function
 * @returns {Object} Sync state and functions
 */
const useRadiodensityMeasurementsSync = ({
  digitalTwinId,
  measurementsByVertebra,
  refetchMeasurements,
}) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const pendingUpdatesRef = useRef({});
  const isSyncingRef = useRef(false);
  const debouncedSyncRef = useRef(null);

  /**
   * Core sync function with lock protection.
   * Checks measurementsByVertebra to decide POST (create) vs PATCH (update).
   * After POST, refetches to get the new IDs immediately.
   */
  const executeSync = useCallback(
    async (updates, existingMeasurements, twinId) => {
      if (!twinId || Object.keys(updates).length === 0) {
        return;
      }

      // Lock: if already syncing, updates stay in pendingUpdatesRef
      if (isSyncingRef.current) {
        return;
      }

      isSyncingRef.current = true;
      setIsSyncing(true);

      const updatesToProcess = { ...updates };
      const processedKeys = Object.keys(updatesToProcess);

      try {
        const toCreate = [];
        const toUpdate = [];

        // Separate into create vs update based on existing measurements
        Object.entries(updatesToProcess).forEach(([vertebraId, data]) => {
          const vertebraNum = parseInt(vertebraId);
          const existing = existingMeasurements[vertebraNum];

          if (existing?._id) {
            // Measurement exists → PATCH
            toUpdate.push({
              _id: existing._id,
              ...data,
            });
          } else {
            // No measurement → POST
            toCreate.push({
              digital_twin_id: twinId,
              vertebra: vertebraNum,
              ...data,
            });
          }
        });

        // Execute create operations
        if (toCreate.length > 0) {
          await createRadiodensityMeasurementInDatabase(toCreate);
          // Refetch immediately to get new IDs
          if (refetchMeasurements) {
            await refetchMeasurements();
          }
        }

        // Execute update operations
        for (const update of toUpdate) {
          const { _id, ...updateData } = update;
          await updateRadiodensityMeasurementInDatabase(_id, updateData);
        }

        // Clear processed updates
        processedKeys.forEach(key => {
          delete pendingUpdatesRef.current[key];
        });
      } catch (error) {
        console.error("Error syncing radiodensity measurements:", error);
      } finally {
        isSyncingRef.current = false;
        setIsSyncing(false);

        // If new updates arrived during sync, process them
        if (Object.keys(pendingUpdatesRef.current).length > 0) {
          debouncedSyncRef.current?.(
            { ...pendingUpdatesRef.current },
            measurementsByVertebra,
            digitalTwinId
          );
        }
      }
    },
    [digitalTwinId, measurementsByVertebra, refetchMeasurements]
  );

  // Initialize debounced sync
  if (!debouncedSyncRef.current) {
    debouncedSyncRef.current = debounce(
      (updates, existingMeasurements, twinId) => {
        executeSync(updates, existingMeasurements, twinId);
      },
      500
    );
  }

  /**
   * Queue a measurement update for syncing.
   */
  const queueMeasurementSync = useCallback(
    (vertebraId, data) => {
      if (!digitalTwinId) {
        return;
      }

      pendingUpdatesRef.current[vertebraId] = {
        ...pendingUpdatesRef.current[vertebraId],
        ...data,
      };

      debouncedSyncRef.current(
        { ...pendingUpdatesRef.current },
        measurementsByVertebra,
        digitalTwinId
      );
    },
    [digitalTwinId, measurementsByVertebra]
  );

  /**
   * Sync all statistics at once.
   */
  const syncAllStatistics = useCallback(
    (statistics, vertebraDimensions) => {
      if (!digitalTwinId || !statistics) {
        return;
      }

      Object.entries(statistics).forEach(([vertebraId, stats]) => {
        const dims = vertebraDimensions[vertebraId] || {};
        queueMeasurementSync(vertebraId, {
          height: dims.height,
          diameter: dims.diameter,
          mean: stats.mean,
          min: stats.min,
          max: stats.max,
        });
      });
    },
    [digitalTwinId, queueMeasurementSync]
  );

  return {
    isSyncing,
    queueMeasurementSync,
    syncAllStatistics,
  };
};

export default useRadiodensityMeasurementsSync;
